import axios from "@/api/axiosInstance";
import { CreateAttemptForCompanyResponse } from "./types";
import type { CoachAttemptDetail } from "../../types/diagnostic";

/**
 * How many times a lost create race is retried. One is enough: the retry takes
 * the resume path, and a second collision would mean a third coach opened the
 * same company in the same instant.
 */
const CONFLICT_RETRIES = 1;

const isConflict = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  (error as { response?: { status?: number } }).response?.status === 409;

const post = async (
  slug: string,
  companyId: number,
  willRetry: boolean,
): Promise<CoachAttemptDetail> => {
  const response = await axios.post<CreateAttemptForCompanyResponse>(
    `/api/private/diagnostics/${slug}/attempts-for-company/${companyId}`,
    undefined,
    // Only a 409 this function is about to recover from is withheld from the
    // user. The last attempt sends no suppression, so a create that loses the
    // race every time toasts like any other error rather than failing silently.
    // Every other status toasts on every attempt.
    willRetry ? { suppressErrorToastForStatus: [409] } : undefined,
  );
  return response.data.data;
};

/**
 * Start a diagnostic for a company, or resume the one already in progress.
 * 201 for a new attempt, 200 for a resumed one — both carry the same payload.
 *
 * Two coaches opening the same company at the same instant both miss the
 * existing-draft lookup and both insert; the partial unique index rejects the
 * loser with a 409. The backend cannot recover from it — a failed flush closes
 * the EntityManager — so the recovery lives here, and the retry takes the
 * resume path onto the winner's draft. It belongs in this function rather than
 * a component because two screens open the dialog.
 */
export const createAttemptForCompany = async (
  slug: string,
  companyId: number,
): Promise<CoachAttemptDetail> => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await post(slug, companyId, attempt < CONFLICT_RETRIES);
    } catch (error) {
      if (attempt >= CONFLICT_RETRIES || !isConflict(error)) {
        throw error;
      }
    }
  }
};
